/**
 * Zeitrechnung für den Demonstrationsstand.
 *
 * **Warum der Bestand relativ erzeugt wird und nicht als Datei liegt.** Ein
 * fest eingetragener Bestand hat genau einen guten Tag: den, an dem er
 * geschrieben wurde. Am Tag darauf ist das Dashboard leer, die Tagesansicht
 * des Monteurs zeigt nichts, und „welche Einsätze sind heute geplant" — die
 * erste Frage, die `07` §7.4 dem Dashboard stellt — hat keine Antwort mehr.
 *
 * Wer den Stand dann beurteilt, beurteilt einen Fehler, den es nicht gibt.
 * Deshalb hängt jeder Zeitpunkt hier an einem Bezugstag, den der Aufrufer
 * setzt: im Normalfall heute, im Test ein fester Tag.
 *
 * Alles rechnet in `Europe/Berlin`. Der Betrieb liegt in Oberfranken, seine
 * Arbeitstage beginnen nicht um 07:00 UTC.
 */

/** Minuten, die `Europe/Berlin` an einem Datum vor UTC liegt. */
function versatzMinuten(datum: Date): number {
  // Ohne Bibliothek und ohne Tabelle: Der Formatierer kennt die Regeln der
  // Zeitzonendatenbank, also lässt man ihn rechnen. Die Sommerzeit springt
  // damit am richtigen Tag um, auch wenn sich die Regel später ändert.
  const alsBerlin = new Date(
    datum.toLocaleString("en-US", { timeZone: "Europe/Berlin" }),
  );
  const alsUtc = new Date(datum.toLocaleString("en-US", { timeZone: "UTC" }));
  return Math.round((alsBerlin.getTime() - alsUtc.getTime()) / 60000);
}

function zweistellig(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Ein Zeitpunkt als ISO-8601-Zeichenkette **mit Zonenversatz**.
 *
 * Der Versatz steht ausdrücklich dran und wird nicht in UTC umgerechnet: Wer
 * beim Nachsehen im Netzwerkverkehr `2026-09-05T08:30:00+02:00` liest, sieht
 * die Uhrzeit, die auf dem Zettel des Monteurs steht. Bei `06:30:00Z` müsste
 * er im Kopf umrechnen, und genau dort entstehen die Fehler, bei denen ein
 * Termin um zwei Stunden verrutscht.
 */
export function zeitpunkt(
  bezugstag: Date,
  tageVersatz: number,
  stunde: number,
  minute = 0,
): string {
  const tag = new Date(bezugstag);
  tag.setDate(tag.getDate() + tageVersatz);

  const jahr = tag.getFullYear();
  const monat = zweistellig(tag.getMonth() + 1);
  const tagImMonat = zweistellig(tag.getDate());

  // Der Versatz wird für den Zieltag bestimmt, nicht für den Bezugstag: Ein
  // Bestand, der über den letzten Oktobersonntag reicht, hätte sonst nach der
  // Umstellung eine Stunde Sprung in den Terminen.
  const versatz = versatzMinuten(new Date(jahr, tag.getMonth(), tag.getDate(), 12));
  const vz = versatz >= 0 ? "+" : "-";
  const va = Math.abs(versatz);

  return (
    `${jahr}-${monat}-${tagImMonat}T${zweistellig(stunde)}:${zweistellig(minute)}:00`
    + `${vz}${zweistellig(Math.floor(va / 60))}:${zweistellig(va % 60)}`
  );
}

/** Nur der Tag, ohne Uhrzeit — für Rechnungsdatum, Fälligkeit, „Kunde seit". */
export function tag(bezugstag: Date, tageVersatz: number): string {
  const d = new Date(bezugstag);
  d.setDate(d.getDate() + tageVersatz);
  return `${d.getFullYear()}-${zweistellig(d.getMonth() + 1)}-${zweistellig(d.getDate())}`;
}

/**
 * Der nächste Werktag ab einem Versatz.
 *
 * Ein Demostand, der die Hälfte seiner Einsätze auf einen Samstag legt, sieht
 * falsch aus, ohne dass man sofort sagen kann warum. Die Funktion verschiebt
 * deshalb auf Montag, wenn der gewünschte Tag aufs Wochenende fällt.
 *
 * **Notdienste dürfen trotzdem am Wochenende liegen** — dafür ruft man sie
 * einfach nicht auf. Ein Handwerksbetrieb ohne einen einzigen Samstagseinsatz
 * wäre genauso unglaubwürdig wie einer, der nur samstags arbeitet.
 */
export function werktag(bezugstag: Date, tageVersatz: number): number {
  const d = new Date(bezugstag);
  d.setDate(d.getDate() + tageVersatz);
  const wochentag = d.getDay();
  if (wochentag === 6) return tageVersatz + 2;
  if (wochentag === 0) return tageVersatz + 1;
  return tageVersatz;
}
